
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { ShieldCheck, Smartphone, Loader2, Copy } from 'lucide-react';
import { errorMessage, reportError } from '../../../lib/errors';

// import { QRCodeSVG } from 'qrcode.react'; // Removed - using Supabase provided QR


interface MFAProps {
    onSuccess: () => void;
}

export const MFAFlow = ({ onSuccess }: MFAProps) => {
    const [mode, setMode] = useState<'loading' | 'enroll' | 'challenge' | 'error'>('loading');
    const [factors, setFactors] = useState<any[]>([]); // Used in challenge flow
    const [enrollData, setEnrollData] = useState<any>(null);
    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [verifying, setVerifying] = useState(false);

    useEffect(() => {
        checkMFAStatus();
    }, []);

    const checkMFAStatus = async () => {
        setError(null);
        try {
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError) throw userError;
            if (!user) throw new Error('Sign in again to verify your identity.');
            const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors();
            if (factorsError) {
                reportError(factorsError, 'mfa-flow.auth-mfa');
                throw factorsError;
            }
            const verifiedFactors = factors?.totp?.filter(f => f.status === 'verified') || [];
            setFactors(verifiedFactors);
            setMode(verifiedFactors.length === 0 ? 'enroll' : 'challenge');
        } catch (err) {
            setError(errorMessage(err));
            setMode('error');
        }
    };

    const startEnrollment = async () => {
        setVerifying(true);
        setError(null);
        try {
            const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
            if (error) throw error;
            setEnrollData(data);
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setVerifying(false);
        }
    };

    const handleVerify = async () => {
        setVerifying(true);
        setError(null);

        try {
            if (mode === 'enroll') {
                if (!enrollData?.id) return;
                const { data: _data, error } = await supabase.auth.mfa.challengeAndVerify({
                    factorId: enrollData.id,
                    code
                });

                if (error) throw error;
                onSuccess();
            } else {
                const factorId = factors[0].id; // Use first available factor
                const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
                    factorId
                });

                if (challengeError) throw challengeError;

                // 2. Verify
                const { data: _data, error } = await supabase.auth.mfa.verify({
                    factorId,
                    challengeId: challengeData.id,
                    code
                });

                if (error) throw error;
                onSuccess();
            }
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setVerifying(false);
        }
    };

    if (mode === 'loading') return <div className="flex h-screen items-center justify-center bg-scrim"><Loader2 className="animate-spin text-success" /></div>;

    return (
        <div className="min-h-screen bg-scrim text-success font-mono flex flex-col items-center justify-center p-4">
            <div className="max-w-md w-full border border-success/30 bg-success/5 p-8 rounded-lg text-center relative overflow-hidden">
                <div className="relative z-10 flex flex-col items-center gap-6">
                    <div className="p-4 rounded-full bg-success/10 border border-success/20">
                        {mode === 'enroll' ? <Smartphone className="w-12 h-12" /> : <ShieldCheck className="w-12 h-12" />}
                    </div>

                    <h1 className="text-2xl font-bold tracking-widest uppercase text-success">
                        {mode === 'enroll' ? 'SETUP SECURITY' : 'VERIFY IDENTITY'}
                    </h1>

                    {mode === 'enroll' && enrollData && (
                        <div className="space-y-4 w-full">
                            <p className="text-sm text-success/70">Scan this QR code with your authenticator app</p>
                            <div data-theme="paper" className="bg-card p-2 rounded-lg w-48 h-48 mx-auto flex items-center justify-center">
                                {/* Supabase returns SVG string, we can inject it or use an image if it's a data URI */}
                                <img src={enrollData.totp.qr_code} alt="QR Code" className="w-full h-full" />
                            </div>
                            <div className="flex items-center gap-2 justify-center text-xs text-success bg-success/10 p-2 rounded">
                                <span className="font-mono">{enrollData.totp.secret}</span>
                                <Copy className="w-3 h-3 cursor-pointer" onClick={() => navigator.clipboard.writeText(enrollData.totp.secret)} />
                            </div>
                        </div>
                    )}

                    <div className="w-full space-y-4">
                        {mode === 'enroll' && !enrollData && (
                            <button onClick={startEnrollment} disabled={verifying} className="w-full py-3 border border-success rounded">
                                {verifying ? 'Preparing authenticator...' : 'Set up authenticator'}
                            </button>
                        )}
                        {mode === 'error' && <button onClick={checkMFAStatus} className="w-full py-3 border border-success rounded">Retry</button>}
                        <input
                            type="text"
                            placeholder="Enter 6-digit code"
                            value={code}
                            onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            className="w-full bg-scrim border border-success/20 rounded p-3 text-center text-2xl tracking-[0.5em] text-success focus:outline-none focus:border-success transition-all font-mono placeholder:tracking-normal placeholder:text-sm placeholder:text-success"
                        />

                        {error && (
                            <div className="text-danger text-xs bg-danger/5 p-2 rounded border border-danger/30 flex items-center gap-2 justify-center">
                                <span className="w-1.5 h-1.5 rounded-full bg-danger-fill" />
                                {error}
                            </div>
                        )}

                        <button
                            onClick={handleVerify}
                            disabled={code.length !== 6 || verifying || mode === 'error' || (mode === 'enroll' && !enrollData)}
                            className={`w-full py-3 rounded font-bold uppercase tracking-widest transition-all
                                ${code.length === 6 && !verifying
                                    ? 'bg-success-fill text-on-fill hover:brightness-110 shadow-soft'
                                    : 'bg-success/5 text-success cursor-not-allowed border border-success/10'
                                }`}
                        >
                            {verifying ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'VERIFY ACCESS'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
